/**
 * CaseDataTypeChart Component
 *
 * A horizontal bar chart component using Chart.js to display case analytics data
 * Shows metrics like Allocated, Attempted, Connected, etc. with both count and percentage views
 * Includes data labels displayed on bars for better visibility
 */

import React, { useState, useMemo, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";
import {
  Box,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  FormControlLabel,
  Switch,
  Button,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { FileDownloadOutlined } from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { metricsConfig, chartStyles, colorUtils } from "../utility/graph";
import { useExportOnlyCaseCategoryAnalytics } from "../../../../../../../features/analytics/hooks/exports";
import { useGetOnlyCaseCategoryAnalyticsRange } from "../../../../../../../features/analytics/hooks/useGetOnlyCaseCategoryAnalytics";
import {
  DATA_TYPE_OPTIONS,
  generateQueryParams,
  getDataTypeValue,
} from "../constants/dataTypeConstants";
import DateRangePicker from "../../../components/date-range-picker";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

/**
 * CaseDataTypeChart Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Analytics data object containing metrics
 * @param {string} props.title - Chart title
 * @param {string} props.dataType - Current data type (All, One, Two)
 * @param {Function} props.onDataTypeChange - Callback for data type change
 * @param {Array} props.dataTypeOptions - Available data type options
 * @param {string} props.initialFromDate - Start date for the data range
 * @param {string} props.initialToDate - End date for the data range
 * @param {Object} props.queryParams - Query parameters for export (year, month, data_type)
 */
export const CaseDataTypeChart = ({
  title = "Case Analytics",
  initialFromDate = dayjs().startOf("month"),
  initialToDate = dayjs().endOf("month"),
  initialDataType = "all",
  onDataTypeChange,
  dataTypeOptions = DATA_TYPE_OPTIONS,
  refreshKey = 0,
}) => {
  const getInitialLabel = () => {
    if (initialDataType === "all") return "All";
    const option = dataTypeOptions.find((o) => o.value === initialDataType);
    return option?.label || dataTypeOptions[0].label;
  };

  // Local date and data type state so each child can control its own filters
  const [fromDate, setFromDate] = useState(
    initialFromDate ? dayjs(initialFromDate) : dayjs().startOf("month"),
  );
  const [toDate, setToDate] = useState(
    initialToDate ? dayjs(initialToDate) : dayjs().endOf("month"),
  );
  const [localDataType, setLocalDataType] = useState(getInitialLabel());
  const [childRefreshKey, setChildRefreshKey] = useState(0);
  // State for toggling between count and percentage view
  const [viewMode, setViewMode] = useState("count");
  // State for toggling data labels display
  const [showDataLabels, setShowDataLabels] = useState(true);

  // Export hook with UX callbacks
  const exportOnlyCaseCategoryAnalytics = useExportOnlyCaseCategoryAnalytics({
    onSuccess: (data) => {
      toast.success(
        `Only case category analytics exported successfully! File: ${data.filename}`,
      );
    },
    onError: (error) => {
      toast.error(`Export failed: ${error.message}`);
    },
  });

  const queryParams = useMemo(() => {
    const params = {
      from: fromDate.format("YYYY-MM-DD"),
      to: toDate.format("YYYY-MM-DD"),
    };
    return generateQueryParams(params, localDataType);
  }, [fromDate, toDate, localDataType]);

  // Fetch data using child's local params
  // Always refetch on mount by using a unique key (forces remount/fetch)
  const [fetchKey] = useState(() => Date.now());

  // Handle export button click
  const handleExport = () => {
    toast.loading("Preparing export...", {
      id: "only-case-category-analytics-export",
    });

    exportOnlyCaseCategoryAnalytics.mutate(queryParams, {
      onSettled: () => {
        toast.dismiss("only-case-category-analytics-export");
      },
    });
  };

  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Handle data type change (local)
  const handleDataTypeChange = (event) => {
    console.log("Data type changed to:", event.target);
    setLocalDataType(event.target.value);
    if (onDataTypeChange) onDataTypeChange(event.target.value);
  };

  // Handle local date change
  const handleFromDateChange = (newDate) => {
    setFromDate(newDate || dayjs().startOf("month"));
  };

  const handleToDateChange = (newDate) => {
    setToDate(newDate || dayjs().endOf("month"));
  };

  // Handle data labels toggle
  const handleDataLabelsToggle = (event) => {
    setShowDataLabels(event.target.checked);
  };

  const {
    data: backendData,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetOnlyCaseCategoryAnalyticsRange(queryParams, fetchKey);

  // Recompute derived frontend data from backend response
  const data = useMemo(() => {
    if (!backendData?.data?.categories) return {};
    const categories = backendData.data.categories;
    return {
      allocated: categories.allocated?.count || 0,
      allocatedPercentage: categories.allocated?.percentage || 0,
      attempted: categories.attempted?.count || 0,
      attemptedPercentage: categories.attempted?.percentage || 0,
      connected: categories.connected?.count || 0,
      connectedPercentage: categories.connected?.percentage || 0,
      unreached: categories.unreached?.count || 0,
      unreachedPercentage: categories.unreached?.percentage || 0,
      childAboveTwo: categories.childAbove2Years?.count || 0,
      childAboveTwoPercentage: categories.childAbove2Years?.percentage || 0,
      notInPortal: categories.notInPortal?.count || 0,
      notInPortalPercentage: categories.notInPortal?.percentage || 0,
      pending: categories.pending?.count || 0,
      pendingPercentage: categories.pending?.percentage || 0,
      active: categories.active?.count || 0,
      activePercentage: categories.active?.percentage || 0,
      unattempted: categories.unattempted?.count || 0,
      unattemptedPercentage: categories.unattempted?.percentage || 0,
    };
  }, [backendData]);

  // If parent triggers refreshKey change, cause a local refetch
  useEffect(() => {
    if (refreshKey !== childRefreshKey) {
      setChildRefreshKey(refreshKey);
      refetch();
    }
  }, [refreshKey, childRefreshKey, refetch]);

  // Prepare chart data based on current view mode
  const chartData = useMemo(() => {
    if (!data || Object.keys(data).length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Determine which field to use based on view mode
    const suffix = viewMode === "percentage" ? "Percentage" : "";

    const labels = metricsConfig.map((metric) => metric.label);
    const values = metricsConfig.map((metric) => {
      const fieldName = metric.key + suffix;
      return data[fieldName] || 0;
    });
    const backgroundColors = metricsConfig.map(
      (metric) => metric.backgroundColor,
    );
    const borderColors = metricsConfig.map((metric) => metric.borderColor);

    return {
      labels,
      datasets: [
        {
          label: viewMode === "percentage" ? "Percentage (%)" : "Count",
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };
  }, [data, viewMode]);

  // Chart options with performance optimizations
  const chartOptions = useMemo(
    () => ({
      indexAxis: "y", // Horizontal bar chart
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
      },
      // Layout padding to accommodate data labels
      layout: {
        padding: {
          right: showDataLabels ? 60 : 10,
          left: 10,
          top: 10,
          bottom: 10,
        },
      },
      // Data labels plugin configuration
      plugins: {
        datalabels: {
          display: showDataLabels,
          align: "end",
          anchor: "end",
          color: function (context) {
            // Use contrasting color based on bar color
            return "#333";
          },
          font: {
            size: 11,
            weight: "bold",
            family: "Roboto, Arial, sans-serif",
          },
          formatter: function (value, context) {
            const suffix = viewMode === "percentage" ? "%" : "";
            if (value <= 0) return "";

            // Format numbers with appropriate decimal places
            const formattedValue =
              viewMode === "percentage"
                ? value.toFixed(1)
                : value.toLocaleString();

            return formattedValue + suffix;
          },
          clip: false,
          clamp: false,
          // Position labels outside the bar for better visibility
          offset: 4,
        },
        legend: {
          display: false, // Hide the legend since category names are on the y-axis
        },
        title: {
          display: true,
          text: `${title} - ${fromDate.format("MMM YYYY")} to ${toDate.format("MMM YYYY")} (${viewMode === "percentage" ? "Percentage View" : "Count View"})`,
          font: chartStyles.fonts.title,
          padding: chartStyles.padding,
        },
        tooltip: {
          ...chartStyles.tooltip,
          callbacks: {
            label: function (context) {
              const value = context.parsed.x;
              const suffix = viewMode === "percentage" ? "%" : "";
              const formattedValue =
                viewMode === "percentage"
                  ? value.toFixed(2)
                  : value.toLocaleString();
              return `${context.dataset.label}: ${formattedValue}${suffix}`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: chartStyles.grid,
          ticks: {
            font: chartStyles.fonts.ticks,
            callback: function (value) {
              const suffix = viewMode === "percentage" ? "%" : "";
              return value.toLocaleString() + suffix;
            },
          },
          title: {
            display: true,
            text: viewMode === "percentage" ? "Percentage (%)" : "Count",
            font: {
              size: 12,
              weight: "bold",
            },
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            font: chartStyles.fonts.ticks,
            maxRotation: 0,
            minRotation: 0,
          },
        },
      },
      // Performance optimizations
      animation: chartStyles.animation,
      elements: {
        bar: {
          borderWidth: 1,
        },
      },
    }),
    [title, viewMode, showDataLabels, fromDate, toDate],
  );

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        border: "1px solid #d0d0d0",
        borderRadius: 2,
      }}
    >
      {/* Chart Controls */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        {/* Left side controls */}
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Local Date Picker - child controls its own date filter */}
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onFromChange={handleFromDateChange}
            onToChange={handleToDateChange}
            disabled={isFetching}
          />
          {/* Data Type Selector */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="data-type-select-label">Data Type</InputLabel>
            <Select
              labelId="data-type-select-label"
              value={localDataType}
              label="Data Type"
              onChange={handleDataTypeChange}
            >
              {dataTypeOptions.map((option) => (
                <MenuItem key={option.label} value={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Data Labels Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={showDataLabels}
                onChange={handleDataLabelsToggle}
                size="small"
              />
            }
            label="Show Values"
            sx={{ ml: 1 }}
          />
        </Stack>

        {/* Right side controls */}
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Manual refresh for the child */}
          <Button
            variant="outlined"
            onClick={() => refetch()}
            disabled={isFetching}
            size="small"
          >
            {isFetching ? "Refreshing..." : "Refresh"}
          </Button>
          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                px: 2,
                py: 1,
                fontSize: "0.875rem",
              },
            }}
          >
            <ToggleButton value="count">Count View</ToggleButton>
            <ToggleButton value="percentage">Percentage View</ToggleButton>
          </ToggleButtonGroup>

          {/* Export Button */}
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlined />}
            onClick={handleExport}
            disabled={
              exportOnlyCaseCategoryAnalytics.isLoading ||
              isFetching ||
              !data ||
              Object.keys(data).length === 0
            }
            size="small"
            sx={{
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": {
                borderColor: "#1565c0",
                backgroundColor: "#f3f4f6",
              },
              "&:disabled": {
                borderColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {exportOnlyCaseCategoryAnalytics.isLoading
              ? "Exporting..."
              : "Export"}
          </Button>
        </Stack>
      </Stack>

      {/* Chart Container */}
      <Box sx={{ height: 500, width: "100%" }}>
        {chartData.labels.length > 0 ? (
          <Bar
            data={chartData}
            options={chartOptions}
            // Performance optimization: prevent unnecessary re-renders
            redraw={false}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <Typography variant="body1">
              No data available for the selected filters
            </Typography>
          </Box>
        )}
      </Box>

      {/* Data Summary */}
      {data && Object.keys(data).length > 0 && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Data Type:</strong> {localDataType} |<strong> View:</strong>{" "}
            {viewMode === "percentage" ? "Percentage" : "Count"} |
            <strong> Labels:</strong> {showDataLabels ? "Visible" : "Hidden"} |
            <strong> Total Metrics:</strong> {metricsConfig.length}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
